import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Download, ShieldAlert } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { fmtMoney } from '../lib/quoteOptions';
import { QUOTE_VIEW_PAGE } from '../constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—');

export default function QuoteView() {
  const { quoteNumber } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(false);

  useDocumentMeta(`Quotation ${quoteNumber} | BulkUsedTires.com`, 'View your wholesale tire quotation, pricing breakdown and shipping terms.');

  useEffect(() => {
    axios
      .get(`${API}/quotes/view/${quoteNumber}/${token}`)
      .then((res) => setQuote(res.data))
      .catch(() => setError(true));
  }, [quoteNumber, token]);

  if (error) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Navbar />
        <div className="pt-40 pb-32 text-center" data-testid={QUOTE_VIEW_PAGE.notFound}>
          <p className="font-mono text-brand-muted text-sm uppercase tracking-wider">Quotation not found or link expired.</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Navbar />
        <div className="pt-40 pb-32 text-center">
          <p className="font-mono text-brand-muted text-sm uppercase tracking-wider">Loading…</p>
        </div>
      </div>
    );
  }

  const pricing = quote.pricing || {};
  const { totals } = quote;

  return (
    <div className="min-h-screen bg-brand-bg">
      <Navbar />
      <section data-testid={QUOTE_VIEW_PAGE.section} className="pt-32 sm:pt-40 pb-24 sm:pb-32">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-widest text-brand-amber mb-3">Quotation</p>
          <h1 className="font-display font-extrabold uppercase text-3xl sm:text-4xl text-brand-text">{quote.quote_number}</h1>
          <p className="mt-2 text-brand-muted text-sm">
            Status: <span className="text-brand-text">{quote.status}</span> · Valid until {pricing.expiration_date || 'N/A'}
          </p>

          {quote.compliance_flag && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 p-4">
              <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm">This destination requires manual compliance review before final confirmation.</p>
            </div>
          )}

          <div className="mt-8 rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-8 space-y-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-brand-muted mb-1">Buyer</p>
              <p className="text-brand-text text-sm">{quote.buyer_name}{quote.company_name ? `, ${quote.company_name}` : ''}</p>
              <p className="text-brand-muted text-sm">{quote.email} · {quote.phone}</p>
            </div>
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-brand-muted mb-1">Shipment</p>
              <p className="text-brand-text text-sm">Destination: {quote.destination_country} — Port of {pricing.destination_port || quote.destination_port}</p>
              <p className="text-brand-muted text-sm">Shipping Terms: {pricing.shipping_terms || 'N/A'} · {quote.grade} {quote.tire_type}</p>
            </div>
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-brand-muted mb-2">Pricing</p>
              <div className="rounded-xl border border-brand-border divide-y divide-brand-border/60">
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-brand-muted">{totals.quantity} tires @ {fmtMoney(totals.unit_price, totals.currency)}</span>
                  <span className="text-brand-text font-mono">{fmtMoney(totals.subtotal, totals.currency)}</span>
                </div>
                {totals.freight > 0 && (
                  <div className="flex justify-between px-4 py-2.5 text-sm">
                    <span className="text-brand-muted">Freight</span>
                    <span className="text-brand-text font-mono">{fmtMoney(totals.freight, totals.currency)}</span>
                  </div>
                )}
                {totals.insurance > 0 && (
                  <div className="flex justify-between px-4 py-2.5 text-sm">
                    <span className="text-brand-muted">Insurance</span>
                    <span className="text-brand-text font-mono">{fmtMoney(totals.insurance, totals.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between px-4 py-3 text-sm font-bold">
                  <span className="text-brand-text">Total</span>
                  <span className="text-brand-orange font-mono">{fmtMoney(totals.total, totals.currency)}</span>
                </div>
              </div>
            </div>
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-brand-muted mb-1">Payment Terms</p>
              <p className="text-brand-muted text-sm">{pricing.payment_terms || 'To be confirmed with your sales contact.'}</p>
            </div>
          </div>

          <a
            href={`${API}/quotes/view/${quoteNumber}/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={QUOTE_VIEW_PAGE.downloadPdfButton}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors"
          >
            <Download className="w-4 h-4" /> Download Quotation PDF
          </a>
          <p className="mt-4 text-center text-brand-muted text-xs">Issued {fmtDate(quote.quoted_at)}</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
